import React, { useEffect, useState, useRef, useMemo } from "react";
import { XCircleIcon, PencilIcon } from "icons/outline";
import EditPopOverForm from "../EditPopOverForm";
import { useRouter } from "next/router";
import { IconButton, cn, OverflowText } from "ui";
import { TableLoader } from "./TableLoader";
import { SidePanelPreviewItem } from "./SidePanelPreviewItem";
import { Table as TanstackTable, flexRender } from "@tanstack/react-table";
import SortIcon from "../SortIcon";

type TableProps = {
  previews: any[];
  customPreview: any;
  applySort: (column: string) => void;
  tableSorts: Record<string, "asc" | "desc">;
  editEnabled: boolean;
  formURL: string;
  tableName: string;
  refreshTable: () => void;
  onRowClick?: (
    rowIndex: number,
    cell: { row: { id: string } },
    preview: any
  ) => void;
  onEditItemClick?: (id: string, row?: any, preview?: any) => void;
  tableInstance: TanstackTable<object>;
  bulkActionErrorIds: string[];
  showSuccessMessage: (message: { name: string; action: string }) => void;
  isLoading: boolean;
  enableRowClickNavigation?: boolean;
  getPreviewPath?: (row: any) => string;
  previewMode?: "side-panel" | "expanded";
  expanded?: boolean;
  getRowId?: (row: any) => string;
};

const Table = React.memo(
  ({
    previews,
    customPreview,
    applySort,
    tableSorts,
    editEnabled,
    formURL,
    tableName,
    refreshTable,
    onRowClick,
    onEditItemClick,
    tableInstance,
    bulkActionErrorIds,
    showSuccessMessage,
    isLoading,
    enableRowClickNavigation,
    getPreviewPath,
    previewMode,
    expanded,
    getRowId,
  }: TableProps) => {
    const router = useRouter();
    // if Content is an object, we will not render it as react component
    const hasFullCustomPreview = typeof customPreview?.Content !== "object";
    const [rowIDIntoPanel, setRowIDIntoPanel] = useState(null);
    const [panelDetails, setPanelDetails] = useState([]);
    const [editPopOverFormOpen, setEditPopOverFormOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const panelDetailsList = useMemo(
      () =>
        Object.entries(panelDetails).map(([key, value]) => ({ key, value })),
      [panelDetails]
    );

    useEffect(() => {
      // reset to not keep visible while paginating
      setRowIDIntoPanel(null);
    }, [isLoading]);

    function onRowClickAction(rowIndex: number, cell: any, preview: any) {
      if (["selection", "option"].indexOf(cell.column.id) !== -1) return; // don't show preview for selection and option column

      if (onRowClick) {
        onRowClick(rowIndex, cell, preview);
      } else {
        setDetailsIntoPanel(rowIndex);
      }
    }

    useEffect(() => {
      if (router.query.preview && previews && !isLoading) {
        const previewIndex = previews.findIndex?.(
          (p) => String(p.id) === router.query.preview
        );
        if (previewIndex >= 0) setDetailsIntoPanel(previewIndex);
        const { preview: _preview, ...queryWithoutPreview } = router.query;
        router.replace({ query: queryWithoutPreview });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.query.preview, previews]);

    // Fixed table column scroll detector
    useEffect(() => {
      if (!containerRef.current) return;

      const handleScroll = (e: any) => {
        const scrollAtEnd =
          e.target.scrollLeft + e.target.offsetWidth >= e.target.scrollWidth;
        e.target.dataset["scrollAtEnd"] = scrollAtEnd;
      };

      handleScroll({ target: containerRef.current });
      const container = containerRef.current;
      container.addEventListener("scroll", handleScroll);

      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }, []);

    function setDetailsIntoPanel(rowIndex: number) {
      const details = previews[rowIndex];

      // toggle panel if previewMode is expanded
      if (previewMode === "expanded") {
        setPanelDetails((prev) => (prev === details ? [] : details));
        setRowIDIntoPanel((prev) =>
          prev === details["id"] ? null : details["id"]
        );
      } else {
        setPanelDetails(details);
        setRowIDIntoPanel(details["id"]);
      }
    }

    const rowIdToSubRowsLength: Record<string, number> = {};

    return (
      <div className="flex flex-row sm:rounded-lg">
        <div
          style={{
            width:
              rowIDIntoPanel === null || previewMode === "expanded"
                ? "100%"
                : "62%",
          }}
        >
          <div
            ref={containerRef}
            className="group relative min-w-full overflow-x-auto border border-foundations-subtle sm:rounded-lg"
          >
            <TableLoader isLoading={isLoading} />
            <table className="min-w-full">
              <thead className="border-b border-inherit">
                {tableInstance.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={cn(
                          `whitespace-nowrap px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-foundations-boldest`,
                          header.column.columnDef.meta?.className,
                          header.column.getCanSort() &&
                            "cursor-pointer select-none"
                        )}
                        colSpan={header.colSpan}
                        onClick={() => {
                          if (header.column.getCanSort()) {
                            applySort(header.column.id);
                          }
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div className="flex items-center">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}

                            {header.column.getCanSort() && (
                              <SortIcon
                                isSorting={!!tableSorts[header.column.id]}
                                isSortedAsc={
                                  tableSorts[header.column.id] === "asc"
                                }
                              />
                            )}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {tableInstance.getRowModel().rows.map((row, rowIndex) => {
                  if (row.getCanExpand()) {
                    rowIdToSubRowsLength[row.id] = row.subRows.length;
                  }
                  const lastSubRowsIds = Object.keys(rowIdToSubRowsLength).map(
                    (key) => {
                      return `${key}.${rowIdToSubRowsLength[key] - 1}`;
                    }
                  );

                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className={cn(
                          "relative cursor-pointer",
                          rowIDIntoPanel === row.id
                            ? "border-b-0 text-foundations-bolder after:absolute after:left-0 after:top-0 after:h-full after:border-l-2 after:border-l-foundations-bolder after:bg-foundations-bolder"
                            : "text-foundations-subtle",
                          rowIDIntoPanel === row.id &&
                            previewMode === "side-panel" &&
                            "shadow",
                          (row.getIsExpanded() || !!row.parentId) &&
                            "bg-foundations-subtelst-02"
                        )}
                      >
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              onClick={(e) => {
                                const targetEl = e.target as HTMLElement;
                                const isLink = targetEl.closest("a");
                                if (isLink) return;

                                if (expanded) {
                                  row.getToggleExpandedHandler()();
                                  return;
                                }

                                if (
                                  enableRowClickNavigation &&
                                  getPreviewPath
                                ) {
                                  const href = getPreviewPath(cell.row);
                                  if (e.metaKey) {
                                    window.open(href, "_blank");
                                  } else {
                                    router.push(href);
                                  }

                                  return;
                                }
                                onRowClickAction(
                                  rowIndex,
                                  cell,
                                  previews[rowIndex]
                                ); // idex>0 to prevent panel details if user click on select
                              }}
                              className={cn(
                                "whitespace-nowrap border-b border-foundations-subtle px-6 py-4 text-sm font-normal",
                                cell.column.columnDef.meta?.className,
                                bulkActionErrorIds.includes(row.id) &&
                                  "!border-danger-emphasis !text-danger",
                                (row.getIsExpanded() ||
                                  (!!row.parentId &&
                                    !lastSubRowsIds.includes(row.id))) &&
                                  "border-b-0"
                              )}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      {previewMode === "expanded" &&
                      rowIDIntoPanel === row.id ? (
                        <tr className="border-b border-b-foundations-subtle">
                          <td colSpan={row.getVisibleCells().length}>
                            {customPreview.Content && (
                              <customPreview.Content preview={panelDetails} />
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {previewMode === "side-panel" && rowIDIntoPanel !== null ? (
          <div
            key={rowIDIntoPanel}
            className="sticky top-5 z-0 ml-8 self-start"
            style={{
              width: "35%",
              minWidth: "316px",
            }}
          >
            <div className="overflow-hidden rounded-md border border-foundations-subtle bg-foundations-default">
              <div className="border-b border-inherit px-6  py-5 sm:px-6">
                <div className="flex items-start justify-between space-x-3">
                  <div className="min-w-0 flex-1">
                    <span className="text-xs font-normal uppercase text-foundations-subtle">
                      {customPreview?.title ?? panelDetailsList[0]?.key}
                    </span>
                    <OverflowText
                      content={
                        <span className="break-words">
                          {panelDetailsList[0]?.value}
                        </span>
                      }
                    >
                      <p className="text-base font-normal text-foundations-boldest">
                        {panelDetailsList[0]?.value}
                      </p>
                    </OverflowText>
                  </div>
                  <div className="flex shrink-0 items-center">
                    {editEnabled && !customPreview?.Actions ? (
                      onEditItemClick ? (
                        <span className="mr-1">
                          <IconButton
                            variant="secondary"
                            size="xs"
                            onClick={() => {
                              const selectedRow = previews.find((p) => {
                                const id = getRowId ? getRowId(p) : p.id;
                                return id === panelDetailsList[0]?.value;
                              });

                              onEditItemClick(
                                panelDetailsList[0]?.value,
                                selectedRow,
                                selectedRow
                              );
                            }}
                          >
                            <PencilIcon className="" aria-hidden="true" />
                          </IconButton>
                        </span>
                      ) : (
                        <EditPopOverForm
                          // @ts-ignore
                          Trigger={({ onClick }) => (
                            <IconButton
                              variant="secondary"
                              className="mr-1"
                              size="xs"
                              onClick={onClick}
                            >
                              <PencilIcon aria-hidden="true" />
                            </IconButton>
                          )}
                          open={editPopOverFormOpen}
                          setOpen={setEditPopOverFormOpen}
                          url={`${formURL}${panelDetailsList[0]?.value}/`}
                          title={`Edit ${tableName}`}
                          // @ts-ignore
                          onSuccess={(name) => {
                            setRowIDIntoPanel(null);
                            showSuccessMessage({ name, action: "edited" });
                            refreshTable();
                          }}
                        />
                      )
                    ) : null}

                    {customPreview?.Actions && (
                      <customPreview.Actions
                        key={rowIDIntoPanel}
                        preview={panelDetails}
                      />
                    )}

                    <IconButton
                      type="button"
                      size="xs"
                      variant="tertiary"
                      onClick={() => setRowIDIntoPanel(null)}
                      aria-label="Close panel"
                    >
                      <XCircleIcon />
                    </IconButton>
                  </div>
                </div>
              </div>

              <div className="max-h-[400px] overflow-y-auto pb-12">
                {customPreview?.Content && hasFullCustomPreview ? (
                  <customPreview.Content
                    key={rowIDIntoPanel}
                    preview={panelDetails}
                  />
                ) : (
                  <div className="px-6 pb-5 sm:px-6">
                    {panelDetailsList.slice(1).map((item) => (
                      <div className="pt-5" key={item.key}>
                        {/* NOTE: don't change itemKey to key as this is a reserved react prop */}
                        {customPreview?.Content?.[item.key]?.(panelDetails) ?? (
                          <SidePanelPreviewItem
                            itemKey={item.key}
                            itemValue={item.value}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="pointer-events-none absolute inset-x-px bottom-px z-20 h-16 rounded-md bg-gradient-to-t from-[theme(backgroundColor.foundations-default)] to-transparent" />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
);
Table.displayName = "Table";
export default Table;
